import { css } from '@emotion/react'
import { memo, useCallback } from 'react'
import { openMenuDialog } from '../../../../actions/menu'
import { openRoom } from '../../../../actions/room'
import { AppStateType, SettingsType } from '../../../../constants/enums'
import { getRecommendationPercentage } from '../../../../mfcLogic/recommendation'
import { getAnimationsEnabledfromUeopt } from '../../../../mfcLogic/settings'
import { event } from '../../../../utilities/analytics'
import { TransitionTypes } from '../../../../utilities/transitions'
import { useAppStateSelector } from '../../../Hooks/useAppState'
import { useModelColor } from '../../../Hooks/useModelColor'
import { useSettingSelector } from '../../../Hooks/useSettings'
import { useUserSelector } from '../../../Hooks/useUser'
import PercentageBar from '../../../common/PercentageBar'
import { palette } from '../../../styles/colors'
import Header from '../../Profile/Header'
import { modelNameStyles } from '../shared'

const rootStyles = css({
    width: '100%',
    maxWidth: '100%',
})
const containerStyles = css({
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    cursor: 'pointer',
    // margin: '5px 0',
})
const youMayLikeContainerStyles = css({
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    height: 90,
    paddingLeft: 5,
})
const avatarPreviewContainerStyles = css({
    flex: '1 0 auto',
    margin: 'auto 0',
})

const topicStyles = css({
    margin: 0,
    marginTop: 1,
    color: palette.secondaryText,
    letterSpacing: '0.00938em',
    fontSize: '0.6rem',
    fontWeight: 400,
    lineHeight: 1.25,
    wordBreak: 'break-all',
})

interface ListProps {
    id: number
    recommendation: number
    inRoomOrProfile?: boolean
}

const List = (props: ListProps) => {
    const { id, recommendation, inRoomOrProfile } = props
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')
    const matchPercentage: number = getRecommendationPercentage(recommendation)
    const [animatePreviews] = useSettingSelector(
        SettingsType.DEFAULT,
        'animatePreviews',
    )
    const [display] = useSettingSelector(SettingsType.DEFAULT, 'display')

    const staticImage = !getAnimationsEnabledfromUeopt(animatePreviews)

    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isOffline = useUserSelector(id, 'isOffline')
    const is16x9 = useUserSelector(id, 'is16x9')
    const albums = useUserSelector(id, 'shareAlbums') ?? 0
    const recordings = useUserSelector(id, 'shareRecordings') ?? 0
    const color = useUserSelector(id, 'color')
    const topic = useUserSelector(id, 'topic')
    const name = useUserSelector(id, 'name')
    const slug = useUserSelector(id, 'slug')

    const modelColor = useModelColor(color)
    const hasShare = albums > 0 || recordings > 0
    const displayName = name || slug

    const onClickOpenMenu = useCallback(() => {
        openMenuDialog(slug, {
            hideNextPrev: true,
            // transitionSource: TransitionTypes.MENU,
            transitionSource: inRoomOrProfile
                ? TransitionTypes.ROOM
                : TransitionTypes.MENU,
        })
    }, [slug, inRoomOrProfile])

    const onClickOpenRoom = useCallback(() => {
        event({
            action: `model_you_may_like_clickthrough`,
            category: 'user_interactions',
            label: 'interaction',
            value: true,
        })
        openRoom(slug, {
            // transitionSource: TransitionTypes.MENU,
            transitionSource: inRoomOrProfile
                ? TransitionTypes.ROOM
                : TransitionTypes.MENU,
        })
    }, [slug, inRoomOrProfile])

    if (!name) {
        return null
    }

    return (
        <section
            key={id}
            css={rootStyles}
            data-testid="ModelsYouMayLike__EmbeddedRecommendationRow"
        >
            <article css={containerStyles}>
                <div css={avatarPreviewContainerStyles}>
                    <Header
                        onClickAvatar={onClickOpenMenu}
                        onClickPreview={onClickOpenRoom}
                        broadcasting={isBroadcasting}
                        displayMode={display}
                        disableBadge={true}
                        staticImage={staticImage}
                        isMobile={isMobile}
                        offline={isOffline}
                        showInfo={false}
                        hasShare={hasShare}
                        is16x9={is16x9}
                        small={true}
                        slug={slug}
                        name={name}
                        id={id}
                    />
                </div>
                <div
                    onClick={onClickOpenRoom}
                    css={youMayLikeContainerStyles}
                    data-testid="ModelsYouMayLike__Recommendation_Details"
                >
                    <span css={modelNameStyles} style={{ color: modelColor }}>
                        {displayName}
                    </span>
                    <PercentageBar
                        percentage={matchPercentage}
                        color={modelColor}
                    />
                    <p css={topicStyles}>{topic}</p>
                </div>
            </article>
        </section>
    )
}

export default memo(List)
